import { Stack, type StackProps, Typography, type TypographyProps } from "~/common/mui";
import { createContext, type ReactNode, useContext } from "react";
import { mergeSlotProps } from "~/common/utils/muiUtils";

export interface ParameterProps {
  className?: string;
  label: ReactNode;
  value: ReactNode;
  reverse?: boolean;
  variant?: "row" | "column";
  slotProps?: {
    label?: TypographyProps;
    value?: TypographyProps;
  };
}

const DEFAULT_LABEL_PROPS: Record<string, TypographyProps> = {
  column: {
    variant: "bodySmall",
    color: "text.label",
  },
  row: {
    variant: "bodyMedium",
    color: "text.label",
  },
};

const DEFAULT_VALUE_PROPS: Record<string, TypographyProps> = {
  column: {
    variant: "bodyLarge",
    color: "text.secondary",
  },
  row: {
    variant: "bodyMedium",
    color: "text.secondary",
  },
};

interface ParameterContextValue {
  parameterVariant?: ParameterProps["variant"];
  slotProps?: ParameterProps["slotProps"];
}

const ParameterContext = createContext<ParameterContextValue | undefined>(undefined);

export const Parameter = ({ label, value, variant, slotProps, reverse, className }: ParameterProps) => {
  const ctx = useContext(ParameterContext);
  const effectiveVariant = variant ?? ctx?.parameterVariant ?? "column";

  const defaultLabelProps = DEFAULT_LABEL_PROPS[effectiveVariant];
  const defaultValueProps = DEFAULT_VALUE_PROPS[effectiveVariant];

  const labelProps = mergeSlotProps(defaultLabelProps, ctx?.slotProps?.label, slotProps?.label);
  const valueProps = mergeSlotProps(defaultValueProps, ctx?.slotProps?.value, slotProps?.value);

  const direction = (effectiveVariant + (reverse ? "-reverse" : "")) as StackProps["direction"];
  return (
    <Stack gap={effectiveVariant === "row" ? 1 : 0.5} direction={direction} className={className}>
      <Typography {...labelProps}>{label}</Typography>
      <Typography {...valueProps}>{value}</Typography>
    </Stack>
  );
};

interface ParameterStackProps extends StackProps, Pick<ParameterProps, "slotProps"> {
  parameterVariant?: ParameterProps["variant"];
}

export const ParameterStack = ({ gap = 0.5, children, parameterVariant, slotProps, ...rest }: ParameterStackProps) => {
  return (
    <ParameterContext.Provider value={{ parameterVariant, slotProps }}>
      <Stack {...rest} gap={gap}>
        {children}
      </Stack>
    </ParameterContext.Provider>
  );
};
